import React, { useEffect, useContext, useMemo } from 'react'
import { useConsumerCart } from 'shop/hooks'
import styled from '@emotion/styled'
import { FulfillmentProps } from './types'
import PickupForm from './Pickup/PickupForm'
import DeliveryForm from './Delivery/DeliveryForm'
import { LandingContext } from './LandingContext'
import { Button } from 'shop/components'
import { hasValidStore, dateString } from 'shop/components/Landing/utils'
import { getStoreUrl } from 'shop/utils/store'
import { StyledHTMLElement } from 'shop/theme/types'
import SelectablePill from './SelectablePill'
import { addDays } from 'date-fns'
import { FulfillmentType } from 'shop/types'
import { BaseAddress, ConsumerCartPayload } from 'shop/types/cart'
import { constructDeliveryAddress, fixFulfillmentTime } from 'shop/utils'
import { useNavigate } from 'react-router-dom'
import { trackFulfillmentTypeChange } from 'shop/utils/common'

const FulfillmentOptions = (
  props: FulfillmentProps & { label: React.ReactNode; hasPadding?: boolean }
) => {
  const landingContext = useContext(LandingContext)
  const navigate = useNavigate()
  const { stores, label, hasPadding } = props

  const {
    fulfillmentDate,
    fulfillmentType,
    setFulfillmentType,
    fulfillmentTime,
    currentLocation,
    currentStore,
    isExtendedHour,
    setHasFailedSubmit,
    setShowFulfillmentOptions,
    validStores,
    loadingDates,
    loadingStores
  } = landingContext

  const { initConsumerCart } = useConsumerCart()

  const deliveryEnabled = hasValidStore(stores, 'delivery')
  const pickupEnabled = hasValidStore(stores, 'pickup')

  const datesOrStoresLoading = useMemo(
    () => loadingDates[fulfillmentType] || loadingStores[fulfillmentType],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadingDates[fulfillmentType], loadingStores[fulfillmentType]]
  )

  useEffect(() => {
    const defaultFulfillment = deliveryEnabled ? 'delivery' : 'pickup'
    setFulfillmentType(defaultFulfillment)
  }, [deliveryEnabled, setFulfillmentType])

  const fixFulfillmentDate = () => {
    const date = isExtendedHour ? addDays(fulfillmentDate, 1) : fulfillmentDate
    return dateString(date)
  }

  const cartParamsValid = (): boolean => {
    const params = createConsumerCartParams()
    if (!params) return false

    const { fulfillmentDate, addressId, deliveryAddress, storeId } = params
    const addressValid =
      fulfillmentType === 'delivery' ? !!deliveryAddress || !!addressId : true

    return !!(
      storeId &&
      fulfillmentTime &&
      fulfillmentDate &&
      addressValid &&
      !!validStores.length
    )
  }

  /**
   * Handles functionality of the submit button
   */
  const handleUserSubmit = (): void | Promise<void> => {
    if (!cartParamsValid()) {
      handleFormValidation()
      return
    }
    return handleInitCart()
  }

  const handleFormValidation = (): void => {
    setHasFailedSubmit(true)
    // Represents the inputs present on the Landing Form, in order.
    const requiredInputs = ['postcode', 'date', 'store', 'time']
    requiredInputs.every((input: string) => {
      const nextRequiredInput = document.getElementById(
        `invalid-required-tag-${input}`
      )
      if (nextRequiredInput) {
        const yOffset = 100
        const newYPos =
          nextRequiredInput.getBoundingClientRect().top +
          window.scrollY -
          yOffset
        window.scrollTo({
          top: newYPos,
          behavior: 'smooth'
        })
        return false
      }
      return true
    })
  }

  const createConsumerCartParams = (): ConsumerCartPayload | null => {
    if (!currentStore) return null
    const addressVars = !!currentLocation?.addressId
      ? {
          addressId: currentLocation.addressId
        }
      : {
          deliveryAddress: constructDeliveryAddress(
            currentLocation,
            fulfillmentType,
            { consumerApi: true }
          ) as BaseAddress
        }
    return {
      storeId: currentStore.id,
      fulfillmentDate: fulfillmentDate && fixFulfillmentDate(),
      fulfillmentTime: fixFulfillmentTime(fulfillmentTime, {
        consumerApi: true
      }),
      fulfillmentType: fulfillmentType.toUpperCase() as FulfillmentType,
      ...addressVars
    }
  }

  const handleInitCart = async () => {
    if (!currentStore) return

    const params = createConsumerCartParams()
    if (params) {
      initConsumerCart({ variables: params }).then((res) => {
        const { cart } = res || {}
        if (cart) {
          navigate(getStoreUrl(currentStore.slug))
        }
      })
    }
  }

  const showFulfillmentOptions = Boolean(deliveryEnabled && pickupEnabled)

  useEffect(() => {
    setShowFulfillmentOptions(!!showFulfillmentOptions)
  }, [showFulfillmentOptions, setShowFulfillmentOptions])

  return (
    <>
      <FormContainer hasTopPadding={hasPadding}>
        {deliveryEnabled && pickupEnabled && label}
        {showFulfillmentOptions && (
          <FulfillmentSelectContainer data-testid='fulfillmentOptions'>
            <SelectablePill
              options={[
                {
                  title: 'Delivery',
                  selected: fulfillmentType === 'delivery',
                  type: 'delivery',
                  onClick: () => {
                    if (datesOrStoresLoading) return
                    setFulfillmentType('delivery')
                    trackFulfillmentTypeChange('DELIVERY', 'landing')
                  }
                },
                {
                  title: 'Pickup',
                  selected: fulfillmentType === 'pickup',
                  type: 'pickup',
                  onClick: () => {
                    if (datesOrStoresLoading) return
                    setFulfillmentType('pickup')
                    trackFulfillmentTypeChange('PICKUP', 'landing')
                  }
                }
              ]}
            />
          </FulfillmentSelectContainer>
        )}
        {fulfillmentType === 'delivery' && <DeliveryForm {...props} />}
        {fulfillmentType === 'pickup' && <PickupForm {...props} />}
      </FormContainer>
      <ButtonContainer>
        <Button
          onClick={handleUserSubmit}
          testId={'continueButton'}
          disabled={!cartParamsValid()}
        >
          Shop now
        </Button>
      </ButtonContainer>
    </>
  )
}

const FormContainer = styled.div<
  StyledHTMLElement & { hasTopPadding?: boolean }
>(({ theme, hasTopPadding = true }) => ({
  // increase width for shallower curve of grey background
  padding: `${hasTopPadding ? theme.space[3] : 0} ${theme.space[3]}px ${theme.space[6] * 2 + 50}px`,
  width: `calc(100% + (${2 * theme.space[3]}px))`,
  transform: `translateX(-${theme.space[3]}px)`,
  position: 'relative',
  overflow: 'hidden',
  flexGrow: 1,

  '> h3': {
    display: 'flex',
    alignItems: 'center',
    margin: '32px 0 18px',
    fontWeight: 'normal',
    fontSize: '20px',
    textTransform: 'uppercase',

    '&:first-of-type': {
      marginTop: '0'
    },

    '> span': {
      flexGrow: 1,
      marginLeft: '1ch',
      fontSize: '14px'
    },

    [theme.mediaQueries.viewport7]: {
      fontWeight: 'bold'
    }
  },

  [theme.mediaQueries.viewport7]: {
    overflow: 'visible',
    width: '100%',
    transform: 'none',
    paddingTop: 0,
    ':before': {
      display: 'none'
    }
  }
}))

const FulfillmentSelectContainer = styled.div<StyledHTMLElement>(
  ({ theme }) => ({
    display: 'flex',
    marginTop: '16px',
    paddingBottom: '32px',
    justifyContent: 'center',
    gap: '20px',
    [theme.mediaQueries.viewport7]: {
      gap: '8px',
      justifyContent: 'left'
    }
  })
)

const ButtonContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  position: 'sticky',
  width: `calc(100% + ${theme.space[3] * 2}px)`,
  transform: `translateX(-${theme.space[3]}px)`,
  bottom: 0,
  padding: `${theme.space[4]}px ${theme.space[3]}px`,
  backgroundColor: 'white',
  borderTop: `1px solid ${theme.colors['lineColor']}`,
  zIndex: theme.zIndex.stickyHeader
}))

export default FulfillmentOptions
